// NOTE: These are now plain async functions that run client-side via the
// browser supabase client (anon key + RLS). This lets the site work on
// Netlify (and any host) without configuring runtime env vars — same model
// as a classic Vite SPA. Public read access is granted via RLS policies
// on products/recipes (status='published' AND deleted_at IS NULL).
import { supabase } from "@/integrations/supabase/client";
import type { Product, Animal, Category } from "@/data/products";
import type { Recipe, RecipeCategory } from "@/data/recipes";

function rowToProduct(row: any): Product {
  const images: string[] = Array.isArray(row.images) ? row.images : [];
  const longDesc: string[] = (row.long_description ?? "")
    .split(/\n\n+/)
    .map((s: string) => s.trim())
    .filter(Boolean);
  const rawSpecs: any[] = Array.isArray(row.characteristics) ? row.characteristics : [];
  const specs = rawSpecs
    .map((s: any) =>
      typeof s === "string"
        ? { label: "", value: s }
        : s && typeof s === "object"
          ? { label: String(s.label ?? ""), value: String(s.value ?? "") }
          : null
    )
    .filter((s): s is { label: string; value: string } => !!s && (!!s.label || !!s.value));
  return {
    slug: row.slug,
    name: row.title,
    category: row.category as Category,
    animal: row.animal as Animal,
    price: Number(row.price),
    unit: row.price_unit === "kg" ? "kg" : "pz",
    shortDescription: row.short_description ?? "",
    longDescription: longDesc.length ? longDesc : [row.short_description ?? ""],
    specs,
    image: images[0] ?? "",
    gallery: images,
    featured: !!row.featured,
    bookable: row.bookable !== false,
    onOffer: !!row.on_offer,
    offerPrice: row.offer_price != null ? Number(row.offer_price) : undefined,
  };
}

function rowToRecipe(row: any): Recipe {
  return {
    slug: row.slug,
    title: row.title,
    excerpt: row.subtitle ?? "",
    cover: row.cover_image ?? "",
    date: (row.published_at ?? row.created_at ?? new Date().toISOString()).slice(0, 10),
    readingTime: row.time_minutes ?? 5,
    category: row.category as RecipeCategory,
    tags: [],
    intro: row.intro ?? "",
    ingredients: Array.isArray(row.ingredients) ? row.ingredients : [],
    steps: Array.isArray(row.steps) ? row.steps : [],
    tips: row.chef_tips ?? undefined,
    productSlugs: Array.isArray(row.product_slugs) ? row.product_slugs : [],
    featured: !!row.featured,
    difficolta: row.difficulty ?? undefined,
    porzioni: row.servings ?? undefined,
    tempoMinutes: row.time_minutes ?? undefined,
  };
}

export async function listPublicProducts() {
  const { data, error } = await supabase
    .from("products")
    .select("*")
    .eq("status", "published")
    .order("sort_order", { ascending: true })
    .order("title", { ascending: true });
  if (error) throw new Error(error.message);
  return { products: (data ?? []).map(rowToProduct) };
}

export type PublicOffer = {
  id: string;
  title: string;
  description: string | null;
  image: string | null;
  cta_text: string | null;
  cta_link: string | null;
};

export async function listPublicOffers(): Promise<{ offers: PublicOffer[] }> {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from("offers")
    .select("id,title,description,image,cta_text,cta_link")
    .eq("type", "popup")
    .eq("active", true)
    .order("created_at", { ascending: false })
    .limit(20);
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<PublicOffer & { starts_at?: string | null; ends_at?: string | null }>;
  // Explicit client-side date filtering: PostgREST chained .or() calls do not
  // combine as an AND of two ORs — they overwrite each other. Filter here to
  // guarantee correct scheduling semantics.
  const filtered = rows.filter((o) => {
    const startsOk = !o.starts_at || o.starts_at <= now;
    const endsOk = !o.ends_at || o.ends_at >= now;
    return startsOk && endsOk;
  });
  return { offers: filtered.slice(0, 1) };
}

export async function getPublicProductBySlug(input: { slug: string }) {
  const { data: row, error } = await supabase
      .from("products")
      .select("*")
      .eq("status", "published")
      .eq("slug", input.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) return { product: null, related: [] };
    const product = rowToProduct(row);
    const { data: relRows } = await supabase
      .from("products")
      .select("*")
      .eq("status", "published")
      .eq("category", row.category)
      .neq("slug", row.slug)
      .limit(4);
    return { product, related: (relRows ?? []).map(rowToProduct) };
}

export async function listPublicRecipes() {
  const { data, error } = await supabase
    .from("recipes")
    .select("*")
    .eq("status", "published")
    .order("published_at", { ascending: false });
  if (error) throw new Error(error.message);
  return { recipes: (data ?? []).map(rowToRecipe) };
}

export async function getPublicRecipeBySlug(input: { slug: string }) {
  const { data: row, error } = await supabase
      .from("recipes")
      .select("*")
      .eq("status", "published")
      .eq("slug", input.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) return { recipe: null, linkedProducts: [], related: [] };
    const recipe = rowToRecipe(row);

    const slugs = recipe.productSlugs;
    let linkedProducts: Product[] = [];
    if (slugs.length) {
      const { data: pRows } = await supabase
        .from("products")
        .select("*")
        .eq("status", "published")
        .in("slug", slugs);
      linkedProducts = (pRows ?? []).map(rowToProduct);
    }

    const { data: relRows } = await supabase
      .from("recipes")
      .select("*")
      .eq("status", "published")
      .eq("category", row.category)
      .neq("slug", row.slug)
      .limit(3);

    return { recipe, linkedProducts, related: (relRows ?? []).map(rowToRecipe) };
}

export async function getRecipesByProductSlug(input: { slug: string }) {
  const { data: rows, error } = await supabase
      .from("recipes")
      .select("*")
      .eq("status", "published")
      .filter("product_slugs", "cs", JSON.stringify([input.slug]))
      .order("published_at", { ascending: false })
      .limit(6);
    if (error) throw new Error(error.message);
    return { recipes: (rows ?? []).map(rowToRecipe) };
}